import { useAtomValue } from 'jotai';
import { crossSectionArmedAtom, sectionModeAtom } from './atoms';
import { CrossSectionHeaderControls } from './cross-section-header-controls';
import { useCrossSection } from './use-cross-section';
import { useCrossSectionRequest } from './use-cross-section-request';
import { useCrossSectionBootstrap } from './use-cross-section-bootstrap';
import { CrossSectionDrawer } from './cross-section-drawer';
import { CrossSectionBody } from './cross-section-body';

/**
 * The whole below-the-map half of the tool: the drawer, and the section in it.
 *
 * A host page places this under its map — inside its own split, so the page keeps
 * control of how much room the map gives up — alongside `CrossSectionMapTool` in the
 * map, `CrossSectionRailSection` in its display rail, and `CrossSectionSettingsPanel`
 * over the stage.
 *
 * Nothing renders until the tool is armed: the drawer is the section's only surface,
 * so there is no drawer without one.
 *
 * @param {object} props - The panel's options.
 * @param {number | null} [props.defaultCollectionId] - A collection the host knows the
 *   section should open on (Area Details: the one covering most of the area).
 * @return {JSX.Element | null} The drawer, or nothing.
 */
export const CrossSectionPanel = ({
  defaultCollectionId = null,
}: {
  defaultCollectionId?: number | null;
} = {}): JSX.Element | null => {
  const armed = useAtomValue(crossSectionArmedAtom);
  const mode = useAtomValue(sectionModeAtom);
  // The same cut the body reads, from the same cache: the header needs to know
  // whether there is a section to act on, and whether one is being worked out.
  const { request, generation } = useCrossSectionRequest();
  const { data, isFetching, error } = useCrossSection(request, generation);
  useCrossSectionBootstrap(defaultCollectionId);

  if (!armed) {
    return null;
  }

  return (
    <CrossSectionDrawer
      headerControls={
        <CrossSectionHeaderControls
          // A section the client asked for, not one the server answered. A failed cut
          // still has a line under it, and the controls are how it is asked about
          // differently — disabling them leaves Clear as the only way out.
          hasSection={mode !== null && (data !== undefined || error !== null)}
          calculating={isFetching}
        />
      }
    >
      <CrossSectionBody />
    </CrossSectionDrawer>
  );
};
